// ...existing code...
import { useMemo, useState } from "react";
import { Combobox, ComboboxInput, ComboboxOption, ComboboxOptions } from "@headlessui/react";
import clsx from "clsx";
import { PRCostCenter, PROwner } from "./patent_renewals";
import _ from "lodash";
import { IconX } from "src/components/icons";
// ...existing code...

// ...existing code...
export function CostCenterSelect({
  owners,
  costCenters,
  value,
  onChange,
  name,
  disabled = false,
  placeholder = "Select a cost center…",
  className = "",
}: {
  owners: PROwner[];
  costCenters: PRCostCenter[];
  value: string | null | undefined;
  onChange: (cc: {target: {name: string, value: any}}) => void;
  name: string;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
}) {
  const [query, setQuery] = useState("");

  const ownersById = useMemo(() => _.keyBy(owners, (o) => o.id), [owners]);

  //console.log({value, onChange})

  const groups = useMemo(() => {
    const norm = (s: string | null | undefined) =>
      (s ?? "").toLowerCase().normalize("NFD")

    const q = norm(query);

    // group all cost centers by owner_id
    return _(costCenters)
      .groupBy((cc) => cc.owner_id)
      .toPairs()
      .map(([owner_id, items]) => {
        const owner = ownersById[owner_id];
        const ownerName = owner?.name ?? "Unknown owner";

        if (!q) {
          return { owner_id, ownerName, items };
        }

        const ownerMatch = norm(ownerName).includes(q);
        const filteredItems = ownerMatch
          ? items // if owner matches, show all its cost centers
          : items.filter((cc) => norm(cc.name).includes(q) || norm(cc.country).includes(q) || norm(cc.invoice_currency).includes(q));

        return filteredItems.length
          ? { owner_id, ownerName, items: filteredItems }
          : null;
      })
      .filter(Boolean)
      .sortBy(g => g.ownerName.toLowerCase())
      .value() as { owner_id: string; ownerName: string; items: PRCostCenter[] }[]
  }, [costCenters, ownersById, query]);

  const displayValue = (cc_id?: string | null) => {
    const cc = costCenters.find((c) => c.id === cc_id);
    if (!cc) return "";
    const ownerName = ownersById[cc.owner_id]?.name ?? "Unknown owner";
    return `${ownerName} — ${cc.name}`;
  };

  return (
    <Combobox immediate value={value ?? null} onChange={value => onChange({target: {name, value}})} disabled={disabled}>
      <div className={clsx("relative", className)}>
        <ComboboxInput
          className={clsx(
            "form-input w-full pr-8",
            disabled && "opacity-50 cursor-not-allowed"
          )}
          displayValue={displayValue}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={placeholder}
        />
        {/* Clear selection */}
        {value && !disabled && (
          <button
            type="button"
            className="absolute inset-y-0 right-2 my-auto size-6 text-slate-400 hover:text-slate-600"
            onClick={() => onChange({target: {name, value: null}})}
            aria-label="Clear selection"
            title="Clear"
          >
            <IconX />
          </button>
        )}

        <ComboboxOptions
          className={clsx(
            "absolute z-10 max-h-72 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-none sm:text-sm"
          )}
        >
          {groups.length === 0 && (
            <div className="cursor-default select-none px-4 py-2 text-slate-500">
              No cost centers found
            </div>
          )}

          {groups.map(({ owner_id, ownerName, items }) => (
            <div key={owner_id}>
              <div className="sticky top-0 z-[1] bg-white/90 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
                {ownerName}
              </div>
              {items.map((cc) => {
                const key = cc.id ?? `${owner_id}:${cc.name}`;
                return (
                  <ComboboxOption
                    key={key}
                    value={cc.id}
                    className={({ active }) =>
                      clsx(
                        "relative cursor-default select-none px-3 py-2 pl-5",
                        active ? "bg-pcx-100 text-pcx-700" : "text-slate-700"
                      )
                    }
                  >
                    {({ selected }) => (
                      <div className="flex flex-col">
                        <div className="flex items-center gap-2">
                          <span
                            className={clsx(
                              "truncate",
                              selected ? "font-semibold" : "font-medium"
                            )}
                          >
                            {cc.name}
                          </span>
                          <span className="text-xs text-slate-500">
                            {cc.invoice_currency}
                          </span>
                        </div>
                        <div className="text-xs text-slate-500">
                          {ownersById[cc.owner_id]?.name ?? "Unknown owner"}
                          {cc.country ? ` - ${cc.country}` : ""}
                        </div>
                      </div>
                    )}
                  </ComboboxOption>
                );
              })}
            </div>
          ))}
        </ComboboxOptions>
      </div>
    </Combobox>
  );
}